import { type MouseEvent, type ReactNode, useState } from "react";
import { DropdownContent, DropdownMenu, DropdownTrigger } from "./dropdown";

/**
 * Right-click menus: the same items as the „…“ menus (DropdownItem & Co.),
 * opened at the pointer. Text fields keep the browser's own menu.
 */

export interface MenuPoint {
  x: number;
  y: number;
}

/** Whether a right click should open our menu (text fields keep the browser's). */
function wantsOwnMenu(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (!target || target.closest("input, textarea, [contenteditable=true]")) return false;
  // Menus and dialogs are portaled, but React still bubbles their events to the
  // wrapper: a click in a dialog opened from here is not a click on the wrapper.
  const layer = target.closest("[role=menu], [role=dialog], [role=alertdialog]");
  return !layer || layer.contains(event.currentTarget as Node);
}

/** Menu at a point of the window, open while `at` is set. */
export function ContextMenuPopup({
  at,
  onClose,
  children,
  className,
}: {
  at: MenuPoint | null;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  if (!at) return null;
  return (
    <DropdownMenu open onOpenChange={(open) => !open && onClose()} modal={false}>
      <DropdownTrigger asChild>
        <span
          aria-hidden="true"
          style={{ position: "fixed", left: at.x, top: at.y, width: 1, height: 1 }}
        />
      </DropdownTrigger>
      <DropdownContent align="start" className={className ?? "min-w-52"}>
        {children}
      </DropdownContent>
    </DropdownMenu>
  );
}

/** State for one menu shared by many rows (e.g. a grid); `open` returns the handler. */
export function useContextMenu<T = true>() {
  const [state, setState] = useState<{ at: MenuPoint; target: T } | null>(null);
  return {
    at: state?.at ?? null,
    target: state?.target ?? null,
    close: () => setState(null),
    /** onContextMenu handler for `target`. */
    open: (target: T) => (event: MouseEvent) => {
      if (!wantsOwnMenu(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setState({ at: { x: event.clientX, y: event.clientY }, target });
    },
  };
}

/** Wraps something so a right click on it opens `menu` (nothing when `menu` is empty). */
export function ContextMenu({
  menu,
  children,
  onOpen,
}: {
  menu: ReactNode;
  children: ReactNode;
  /** Before opening, e.g. to select the row that was clicked. */
  onOpen?: () => void;
}) {
  const [at, setAt] = useState<MenuPoint | null>(null);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the same actions are in the „…“ menu or on the element
    <div
      className="contents"
      onContextMenu={(event) => {
        if (!menu || !wantsOwnMenu(event)) return;
        event.preventDefault();
        event.stopPropagation();
        onOpen?.();
        setAt({ x: event.clientX, y: event.clientY });
      }}
    >
      {children}
      <ContextMenuPopup at={at} onClose={() => setAt(null)}>
        {menu}
      </ContextMenuPopup>
    </div>
  );
}
